import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { useScope } from "@state/scopeContext.lib";
import { useCallback, useMemo } from "react";
import { userWorkspaceRolesInOrder } from "@common/userWorkspaceRoles";
import { sortBy } from "@common/helpers/collections";
import { MemberSchema } from "@common/schemas/client.schema";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { useCanPerformOrganizationAction } from "@hooks/permissions/organization/useCanPerformOrganizationAction";
import { OrganizationAction } from "@common/userOrganizationRoles";
import { Button } from "@design-system/Button/Button";
import { PlusIcon } from "@icons";
import { CaptureClicks } from "@design-system/CaptureClicks/CaptureClicks";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { useOverlays } from "contexts/OverlayContext";
import styles from "../ClientsView.module.scss";
import { ClientTeamList } from "../../TeamList/ClientTeamList";
import { EMPTY_CELL_VALUE } from "./lib";

interface TeamCellRow {
    id: string;
    type: "workspace" | "entity" | "close";
    workspaceId?: string;
    members?: MemberSchema[];
}

export const TeamCell = <TRow extends TeamCellRow>({
    row,
}: CellContext<ViewTableFeatures, TRow, unknown>) => {
    const { organizationId } = useScope();
    const activeOrganization = useActiveOrganization();
    const canAddTeam = useCanPerformOrganizationAction(
        OrganizationAction.INVITE_TEAM_MEMBER,
    );
    const { openOverlay } = useOverlays();

    const team = useMemo(() => {
        const organizationTeam = (row.original.members ?? []).filter(
            (member) => member.organizationId === organizationId,
        );

        return sortBy(
            organizationTeam,
            (member) => userWorkspaceRolesInOrder.indexOf(member.role),
            (member) => member.preferredName,
        );
    }, [row.original.members, organizationId]);

    const addMemberComponent = useCallback(() => {
        const options =
            row.original.type === "workspace"
                ? {
                      initialWorkspaceIds: [
                          row.original.workspaceId ?? row.original.id,
                      ],
                  }
                : undefined;

        const handleAddMember = () => {
            if (!activeOrganization) {
                return;
            }
            openOverlay("organizationAddTeamMember", {
                organization: activeOrganization,
                initialWorkspaceIds: options?.initialWorkspaceIds,
            });
        };

        return !team.length ? (
            <EditableCellContent isEmpty onClick={handleAddMember} />
        ) : (
            <Button
                variant="primary"
                iconOnly
                size="sm"
                className={styles.addMember}
                onClick={handleAddMember}
                aria-label="Add team member"
            >
                <PlusIcon />
            </Button>
        );
    }, [
        team,
        row.original.type,
        row.original.id,
        row.original.workspaceId,
        activeOrganization,
        openOverlay,
    ]);

    if (row.original.type !== "workspace") {
        return EMPTY_CELL_VALUE;
    }

    // activeOrganization will always be available in the client view, but this helps with type safety
    if (!activeOrganization) {
        return null;
    }

    return (
        <CaptureClicks>
            <ClientTeamList
                members={team}
                iconSize="sm"
                hideRole
                appendContent={canAddTeam ? addMemberComponent() : null}
            />
        </CaptureClicks>
    );
};
